/**
 * `/projects/{id}` — an index redirect, not a screen (`KLQ-20260819-004` §3).
 *
 * It sends the caller to the first tab that is both **permitted** (the server's answer) and
 * **built** (ours, `tabs.ts:ROUTES`). Today that is People for admin, manager and architect;
 * for everybody else there is nothing yet, and they get the shell's empty panel rather than
 * a redirect loop or a `403` on a project they are legitimately on.
 *
 * `replace` on the navigation: the index is a decision, not a place, and leaving it in the
 * history means Back lands on it and immediately decides again.
 */

import { Navigate } from '@tanstack/react-router';

import { ProjectNothingBuilt, ProjectShell } from './ProjectShell';
import { useProjectSurfaces } from './queries';
import { firstReachableTab, routeOf } from './tabs';

export function ProjectIndexPage({ projectId }: { readonly projectId: string }) {
  const surfaces = useProjectSurfaces(projectId);

  const destination = surfaces.isSuccess ? firstReachableTab(surfaces.data) : null;
  const to = destination === null ? undefined : routeOf(destination);

  if (to !== undefined) {
    return <Navigate to={to} params={{ projectId }} replace />;
  }

  // Still loading, or loaded and there is nowhere to go. The shell renders its own pending
  // state through the project query, so the panel below is only reached once the answer is
  // in — and then it is the truth rather than a placeholder.
  return (
    <ProjectShell projectId={projectId} tab={null}>
      {surfaces.isPending ? null : <ProjectNothingBuilt />}
    </ProjectShell>
  );
}
